import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Grid,
    Autocomplete,
    TextField,
    Button,
    IconButton,
    Stack,
    CircularProgress,
    Chip,
    Tabs,
    Tab,
} from '@mui/material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany, ILeaderShip, IPosition } from '../../../redux/company/types';
import { useDispatch, useSelector } from 'react-redux';
import { Close, CheckCircle } from '@mui/icons-material';
import { LEADERSHIP_POSITIONS } from '../../../data/forms/company.data';
import { searchUser } from '../../../redux/user/actions';
import { userSearchSelector, searchingUserSearchSelector } from '../../AvatarGroup/selector';
import { ThspianAvatar } from '../../Avatar/ThspianAvatar';
import _ from 'lodash';
import { createPosition, approvePosition, deletePosition } from '../../../redux/company/actions';
import { IUserSummary } from '../../../types/global/helper';

interface ICompanyLeadershipEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
    leadership?: ILeaderShip;
    otherPositions?: ILeaderShip;
}

const CompanyLeadershipEdit: React.FC<ICompanyLeadershipEdit> = ({
    company,
    isEditing,
    onSave,
    onChanges,
    leadership,
    otherPositions,
}) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const userResults = useSelector(userSearchSelector);
    const searchingUser = useSelector(searchingUserSearchSelector);

    const [activeTab, setActiveTab] = useState(0);
    const [newLeader, setNewLeader] = useState<{ title: string; user: IUserSummary | null }>({
        title: '',
        user: null,
    });

    // Get leadership positions from company
    const leadershipPositions = (leadership && leadership.data) || [];
    const otherPositionsData = (otherPositions && otherPositions.data) || [];

    useEffect(() => {
        // No changes tracking needed since we're using direct API calls
        onChanges(false);
    }, [onChanges]);

    const addLeader = () => {
        if (newLeader.title.trim() && newLeader.user && company?._id) {
            const payload = {
                company: company._id,
                user: newLeader.user._id,
                title: newLeader.title,
            };
            dispatch(createPosition(payload));
            setNewLeader({ title: '', user: null });
        }
    };

    const handleApprovePosition = (positionId: string) => {
        dispatch(approvePosition({ positionId }));
    };

    const handleDeletePosition = (positionId: string) => {
        dispatch(deletePosition({ positionId }));
    };

    const handleSave = () => {
        // No save function needed since we're using direct API calls
    };

    // Register the save function with the parent container
    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave]);

    const handleUserSearch = useRef(
        _.debounce((searchTerm: string) => {
            if (searchTerm.length > 3 && !searchingUser) {
                dispatch(searchUser({ searchTerm, withProfileSearch: true }));
            }
        }, 1000),
    ).current;

    const renderPositionList = (positions: IPosition[], title: string) => (
        <Grid item xs={12}>
            <Typography
                variant="h6"
                sx={{
                    color: theme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    mb: 2,
                }}>
                {title}
            </Typography>
            {positions.length > 0 ? (
                <Stack spacing={2}>
                    {positions.map((position: IPosition, index: number) => (
                        <Box
                            key={index}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={2}
                            sx={{
                                backgroundColor: theme[themeStyle].form_black,
                                borderRadius: 2,
                                border: `1px solid ${theme[themeStyle].form_divider}`,
                            }}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <ThspianAvatar user={position.user} sx={{ width: 40, height: 40 }} />
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: theme[themeStyle].tag_white,
                                            fontSize: 14,
                                        }}>
                                        {position.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].lightWhite,
                                            fontSize: 12,
                                        }}>
                                        {position.user.firstName} {position.user.lastName}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                        <Chip
                                            label={position.approved ? 'Approved' : 'Pending Approval'}
                                            size="small"
                                            color={position.approved ? 'success' : 'warning'}
                                            sx={{
                                                backgroundColor: position.approved
                                                    ? theme[themeStyle].green_status_indicator_full
                                                    : theme[themeStyle].yellow_status_indicator_full,
                                                color: theme[themeStyle].black,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: '0.75rem',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            <Box display="flex" alignItems="center" gap={1}>
                                {isEditing && !position.approved && (
                                    <IconButton
                                        onClick={() => handleApprovePosition(position._id)}
                                        sx={{
                                            color: theme[themeStyle].green_status_indicator_full,
                                            '&:hover': {
                                                backgroundColor: theme[themeStyle].green_status_indicator_full + '20',
                                            },
                                        }}>
                                        <CheckCircle fontSize="small" />
                                    </IconButton>
                                )}
                                {isEditing && (
                                    <IconButton
                                        onClick={() => handleDeletePosition(position._id)}
                                        sx={{
                                            color: theme[themeStyle].primary_red,
                                            '&:hover': {
                                                backgroundColor: theme[themeStyle].primary_red + '20',
                                            },
                                        }}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Stack>
            ) : (
                <Typography
                    sx={{
                        color: theme[themeStyle].lightWhite,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontStyle: 'italic',
                    }}>
                    No {title.toLowerCase()} members added yet
                </Typography>
            )}
        </Grid>
    );

    const renderAddForm = () => (
        <Grid item xs={12}>
            <Box
                p={2}
                sx={{
                    backgroundColor: theme[themeStyle].form_black,
                    borderRadius: 2,
                    border: `1px solid ${theme[themeStyle].form_divider}`,
                }}>
                <Typography
                    variant="h6"
                    sx={{
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        mb: 2,
                    }}>
                    Add New Member
                </Typography>

                {/* Position Selection */}
                <Autocomplete
                    options={LEADERSHIP_POSITIONS}
                    freeSolo
                    filterOptions={(options, { inputValue }) => {
                        const filtered = options.filter(option =>
                            option.toLowerCase().includes(inputValue.toLowerCase()),
                        );
                        return filtered;
                    }}
                    value={newLeader.title || ''}
                    onChange={(_, newValue) => {
                        setNewLeader(prev => ({
                            ...prev,
                            title: typeof newValue === 'string' ? newValue : '',
                        }));
                    }}
                    onInputChange={(_, newInputValue) => {
                        setNewLeader(prev => ({
                            ...prev,
                            title: newInputValue,
                        }));
                    }}
                    getOptionLabel={option => option}
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, 0],
                                    },
                                },
                            ],
                        },
                        paper: {
                            sx: {
                                backgroundColor: theme[themeStyle].dropdown_black,
                                border: `1px solid ${theme[themeStyle].form_divider}`,
                                borderRadius: '8px',
                                maxHeight: 300,
                                margin: 0,
                            },
                        },
                    }}
                    renderInput={params => (
                        <TextField
                            {...params}
                            placeholder="Search and Select Position"
                            label="Position"
                            InputLabelProps={{
                                sx: {
                                    color: theme[themeStyle].grey,
                                },
                            }}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].grey,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                                '& .MuiSvgIcon-root': {
                                    color: theme[themeStyle].primary_white,
                                },
                            }}
                            InputProps={{
                                ...params.InputProps,
                                style: {
                                    background: theme[themeStyle].gray_text,
                                    color: theme[themeStyle].lightWhite,
                                    borderRadius: 8,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    border: `1px solid ${theme[themeStyle].form_gray}`,
                                },
                            }}
                        />
                    )}
                    renderOption={(props, option, { selected, index }) => (
                        <li
                            {...props}
                            key={`${option}-${props.id}-${index}`}
                            style={{
                                backgroundColor: selected
                                    ? theme[themeStyle].highlight
                                    : theme[themeStyle].dropdown_black,
                                cursor: 'pointer',
                                padding: 0,
                                margin: 0,
                                borderBottom: `1px solid ${theme[themeStyle].form_divider}`,
                            }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%',
                                    py: 1,
                                    px: 1.5,
                                    backgroundColor: selected ? theme[themeStyle].highlight : 'transparent',
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].form_divider,
                                    },
                                }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].tag_white,
                                    }}>
                                    {option}
                                </Typography>
                            </Box>
                        </li>
                    )}
                    sx={{
                        '& .MuiAutocomplete-paper': {
                            backgroundColor: theme[themeStyle].dropdown_black,
                            border: `1px solid ${theme[themeStyle].form_divider}`,
                            borderRadius: '8px',
                            maxHeight: 300,
                        },
                        '& .MuiAutocomplete-option': {
                            color: theme[themeStyle].primary_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            padding: 2,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].form_divider,
                            },
                            '&.Mui-focused': {
                                backgroundColor: theme[themeStyle].highlight,
                            },
                        },
                        width: '100%',
                        mb: 2,
                    }}
                />

                {/* User Search */}
                <Autocomplete
                    options={userResults}
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, 0],
                                    },
                                },
                            ],
                        },
                        paper: {
                            sx: {
                                backgroundColor: theme[themeStyle].dropdown_black,
                                border: `1px solid ${theme[themeStyle].form_divider}`,
                                borderRadius: '8px',
                                maxHeight: 300,
                                margin: 0,
                            },
                        },
                    }}
                    getOptionLabel={option => {
                        if (typeof option === 'string') return option;
                        return `${option.firstName} ${option.lastName}`;
                    }}
                    value={newLeader.user}
                    onChange={(_, newValue: any) => {
                        setNewLeader(prev => ({
                            ...prev,
                            user: newValue || null,
                        }));
                    }}
                    onInputChange={(_, newInputValue) => {
                        handleUserSearch(newInputValue);
                    }}
                    freeSolo
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    renderInput={params => (
                        <TextField
                            {...params}
                            placeholder="Search and Select User"
                            label="Search and Select User"
                            InputLabelProps={{
                                sx: {
                                    color: theme[themeStyle].grey,
                                },
                            }}
                            sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].grey,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                                '& .MuiSvgIcon-root': {
                                    color: theme[themeStyle].primary_white,
                                },
                                '& .MuiInputBase-input': {
                                    color: theme[themeStyle].lightWhite,
                                },
                            }}
                            InputProps={{
                                ...params.InputProps,
                                style: {
                                    background: theme[themeStyle].gray_text,
                                    color: theme[themeStyle].lightWhite,
                                    borderRadius: 8,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    border: `1px solid ${theme[themeStyle].form_gray}`,
                                },
                                endAdornment: (
                                    <>
                                        {searchingUser ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option, { selected, index }) => (
                        <li
                            {...props}
                            key={`${option._id}-${index}`}
                            style={{
                                backgroundColor: selected
                                    ? theme[themeStyle].highlight
                                    : theme[themeStyle].dropdown_black,
                                cursor: 'pointer',
                                padding: 0,
                                margin: 0,
                                borderBottom: `1px solid ${theme[themeStyle].form_divider}`,
                            }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%',
                                    py: 1,
                                    px: 1.5,
                                    backgroundColor: selected ? theme[themeStyle].highlight : 'transparent',
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].form_divider,
                                    },
                                }}>
                                <Stack direction="row" alignItems="center" gap={1} mb={1}>
                                    <ThspianAvatar user={option} sx={{ width: 30, height: 30 }} />
                                    <Typography sx={{ color: theme[themeStyle].tag_white }}>
                                        {option.firstName} {option.lastName}
                                    </Typography>
                                </Stack>
                            </Box>
                        </li>
                    )}
                    sx={{
                        '& .MuiAutocomplete-paper': {
                            backgroundColor: theme[themeStyle].dropdown_black,
                            border: `1px solid ${theme[themeStyle].form_divider}`,
                            borderRadius: '8px',
                            maxHeight: 300,
                        },
                        '& .MuiAutocomplete-option': {
                            color: theme[themeStyle].primary_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            padding: 2,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].form_divider,
                            },
                            '&.Mui-focused': {
                                backgroundColor: theme[themeStyle].highlight,
                            },
                        },
                        width: '100%',
                        mb: 2,
                    }}
                />

                {/* Action Buttons */}
                <Box display="flex" gap={1}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setNewLeader({
                                title: '',
                                user: null,
                            });
                        }}
                        sx={{
                            color: theme[themeStyle].tag_white,
                            borderColor: theme[themeStyle].tag_white,
                            '&:hover': {
                                borderColor: theme[themeStyle].primary_red,
                                color: theme[themeStyle].primary_red,
                            },
                        }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={addLeader}
                        disabled={!newLeader.title || !newLeader.user}
                        sx={{
                            backgroundColor: theme[themeStyle].highlight,
                            color: theme[themeStyle].black,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].primary_orange,
                            },
                            '&:disabled': {
                                backgroundColor: theme[themeStyle].darkGray60,
                                color: theme[themeStyle].lightWhite,
                            },
                        }}>
                        Add Member
                    </Button>
                </Box>
            </Box>
        </Grid>
    );

    return (
        <Card sx={{ bgcolor: theme[themeStyle].secondaryBg, border: `1px solid ${theme[themeStyle].tag_white_light}` }}>
            <CardHeader
                title="Team Management"
                sx={{ color: theme[themeStyle].tag_white }}
                titleTypographyProps={{
                    sx: { fontFamily: FONT_ENUM.MAIN_BOLD },
                }}
            />
            <CardContent>
                {isEditing ? (
                    // Edit mode with tabs
                    <Box>
                        <Tabs
                            value={activeTab}
                            onChange={(_, newValue) => setActiveTab(newValue)}
                            sx={{
                                '& .MuiTab-root': {
                                    color: theme[themeStyle].lightWhite,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    '&.Mui-selected': {
                                        color: theme[themeStyle].tag_white,
                                    },
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: theme[themeStyle].highlight,
                                },
                            }}>
                            <Tab label={`Leadership (${leadershipPositions.length})`} />
                            <Tab label={`Other Positions (${otherPositionsData.length})`} />
                        </Tabs>

                        <Box sx={{ mt: 3 }}>
                            {activeTab === 0 && (
                                <Grid container spacing={3}>
                                    {renderPositionList(leadershipPositions, 'Current Leadership')}
                                    {renderAddForm()}
                                </Grid>
                            )}
                            {activeTab === 1 && (
                                <Grid container spacing={3}>
                                    {renderPositionList(otherPositionsData, 'Other Positions')}
                                </Grid>
                            )}
                        </Box>
                    </Box>
                ) : (
                    // View mode - show both sections
                    <Grid container spacing={3}>
                        {renderPositionList(leadershipPositions, 'Current Leadership')}
                        {otherPositionsData.length > 0 && renderPositionList(otherPositionsData, 'Other Positions')}
                    </Grid>
                )}

                {/* Note about editing */}
                <Grid item xs={12}>
                    <Box
                        sx={{
                            backgroundColor: theme[themeStyle].form_black,
                            border: `1px solid ${theme[themeStyle].form_divider}`,
                            borderRadius: 2,
                            p: 2,
                            mt: 2,
                        }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].lightWhite,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: '0.875rem',
                            }}>
                            Note: New positions will be created as pending and require approval. You can approve or
                            delete positions using the buttons next to each entry.
                        </Typography>
                    </Box>
                </Grid>
            </CardContent>
        </Card>
    );
};

export { CompanyLeadershipEdit };
